import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Flex, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { JsmSolutionComposerPlanSummarySection } from '@atlassian/conversation-assistant-message-actions-api/jsm';

import {
	CardMetadataSummary,
	type CardMetadataSummaryItem,
} from '../card-metadata-summary/CardMetadataSummary';

import { messages } from './messages';

const MAX_BODY_HEIGHT_BEFORE_TRUNCATE_PX = 242;

const styles = cssMap({
	contentSectionOuter: {
		position: 'relative',
		width: '100%',
		alignSelf: 'stretch',
	},
	contentInner: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		alignSelf: 'stretch',
		boxSizing: 'border-box',
		gap: token('space.200'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	contentInner_clamped: {
		maxHeight: `${MAX_BODY_HEIGHT_BEFORE_TRUNCATE_PX}px`,
		overflowY: 'hidden',
	},
	sectionList: {
		alignSelf: 'stretch',
		minWidth: '0px',
	},
	fadeOffZone: {
		position: 'absolute',
		left: 0,
		right: 0,
		bottom: 0,
		height: '32px',
		width: '100%',
		backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0), ${token('elevation.surface')} 100%)`,
		zIndex: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	showMoreButton: {
		borderRadius: token('radius.xlarge'),
		boxShadow: token('elevation.shadow.overlay'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
		color: token('color.text.subtle'),
		backgroundColor: token('elevation.surface'),
		paddingBlock: token('space.050'),
		paddingInline: token('space.150'),
		zIndex: 100,
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
		},
	},
});

const ShowMoreButton = ({ onExpand }: { onExpand: () => void }): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.fadeOffZone}>
			<Pressable
				xcss={styles.showMoreButton}
				onClick={onExpand}
				testId="jsm-solution-composer-plan-task-card.content-expand-button"
			>
				{formatMessage(messages.expandBodyContent)}
			</Pressable>
		</Box>
	);
};

export type TaskCardContentProps = {
	sections: JsmSolutionComposerPlanSummarySection[];
	summaryItems?: CardMetadataSummaryItem[];
};

export const TaskCardContent = ({
	sections,
	summaryItems,
}: TaskCardContentProps): React.JSX.Element => {
	const [hasUserExpandedBody, setHasUserExpandedBody] = useState(false);
	const [hasTruncatableBodyContent, setHasTruncatableBodyContent] = useState(false);
	const contentInnerRef = useRef<HTMLDivElement>(null);

	const measureBodyOverflow = useCallback(() => {
		const el = contentInnerRef.current;
		if (!el) {
			return;
		}
		setHasTruncatableBodyContent(el.scrollHeight > MAX_BODY_HEIGHT_BEFORE_TRUNCATE_PX);
	}, []);

	useEffect(() => {
		setHasUserExpandedBody(false);
	}, [sections]);

	useLayoutEffect(() => {
		measureBodyOverflow();
	}, [measureBodyOverflow, sections]);

	useLayoutEffect(() => {
		const el = contentInnerRef.current;
		if (!el || typeof ResizeObserver === 'undefined') {
			return undefined;
		}
		const observer = new ResizeObserver(() => {
			measureBodyOverflow();
		});
		observer.observe(el);
		return () => {
			observer.disconnect();
		};
	}, [measureBodyOverflow]);

	const isClamped = hasTruncatableBodyContent && !hasUserExpandedBody;

	const handleExpandBody = useCallback(() => {
		setHasUserExpandedBody(true);
	}, []);

	return (
		<Box xcss={styles.contentSectionOuter}>
			<Box
				ref={contentInnerRef}
				testId="jsm-solution-composer-plan-task-card.content"
				xcss={cx(styles.contentInner, isClamped && styles.contentInner_clamped)}
			>
				{summaryItems && summaryItems.length > 0 ? (
					<CardMetadataSummary items={summaryItems} />
				) : null}
				<Flex direction="column" gap="space.200" xcss={styles.sectionList}>
					{sections.map((section) => (
						<Flex
							key={section.title}
							direction="column"
							alignItems="start"
							gap="space.100"
							xcss={styles.sectionList}
						>
							<Heading size="xsmall">{section.title}</Heading>
							<Text size="medium" weight="regular" color="color.text">
								{section.description}
							</Text>
						</Flex>
					))}
				</Flex>
			</Box>
			{isClamped ? <ShowMoreButton onExpand={handleExpandBody} /> : null}
		</Box>
	);
};
